import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";
import {
  ArrowUpRight,
  Code2,
  Smartphone,
  Globe,
  Users,
  Network,
  BarChart3,
  Rocket,
  Sparkles,
  ShoppingBag,
  Stethoscope,
  Briefcase,
  GraduationCap,
  Building2,
  Truck,
  Lightbulb,
  Zap,
  Shield,
  GitBranch,
} from "lucide-react";

const ICONS = { Code2, Smartphone, Globe, Users, Network, BarChart3, Rocket, Lightbulb };

const HERO_BG = "/img/hero-bg.png";

const INDUSTRIES = [
  { name: "Retail & E-commerce", icon: ShoppingBag, img: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Servicios", icon: Briefcase, img: "https://images.unsplash.com/photo-1551434678-e076c223a692?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Salud & Educación", icon: Stethoscope, img: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { name: "Empresas & Logística", icon: Building2, img: "https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
];

const STATS = [
  { value: "12+", label: "Años en operación" },
  { value: "21d", label: "MVP promedio" },
  { value: "85", label: "Sistemas en producción" },
  { value: "IA", label: "Friendly Development" },
];

const CAPABILITIES = ["Software a Medida", "Apps Móviles", "Aplicaciones Web", "Pilotos & MVPs", "IA-Friendly", "Software Factory", "Digitalización", "Rápidos y Competitivos", "Data & BI", "Producto Digital"];

export default function Home() {
  useSeo({
    title: "DoSoft · Software Factory en Chile · Software a medida, Apps y MVPs",
    description: "Desarrollamos software a medida, apps móviles, aplicaciones web, MVPs y pilotos para empresas que necesitan transformación digital. Cotiza tu proyecto con DoSoft Chile.",
    canonical: "https://dosoft.cl/",
  });
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/projects", { params: { featured: true } }).then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-end overflow-hidden noise"
      >
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-800 via-ink-800/85 to-ink-800/40" />
          <div className="absolute inset-0 grid-lines opacity-30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-3 mb-10 border border-ink-500 px-4 py-2">
              <span className="w-2 h-2 bg-brand animate-pulse" />
              <span className="text-xs uppercase tracking-widest text-ash-300 font-mono">
                DoSoft · Software Factory
              </span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] text-ash-100"
            >
              Construimos el<br />
              <span className="text-brand">software a medida</span><br />
              que tu empresa necesita.
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-lg md:text-xl text-ash-300 mt-8 max-w-2xl leading-relaxed"
            >
              Apps móviles, aplicaciones web y software a medida para empresas de
              cualquier sector. Diseñamos, construimos y mantenemos el producto
              digital que tu negocio necesita.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-12">
              <Link
                to="/cotizador"
                data-testid="hero-cta-quote"
                className="group bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-8 py-4 transition-colors uppercase tracking-wider text-sm inline-flex items-center justify-center gap-3"
              >
                Cotizar Proyecto
                <ArrowUpRight size={18} strokeWidth={2} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
              <Link
                to="/servicios"
                data-testid="hero-cta-services"
                className="border border-ink-500 hover:border-brand bg-transparent text-ash-100 px-8 py-4 transition-colors uppercase tracking-wider text-sm inline-flex items-center justify-center gap-3"
              >
                Ver capacidades
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom meta strip */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-ink-500 bg-ink-800/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-widest font-mono text-ash-300">
            <span>SCROLL ↓ EXPLORE</span>
            <span className="hidden md:block">SANTIAGO · CL</span>
            <span>2026 / DOSOFT.CL</span>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section data-testid="capabilities-marquee" className="border-y border-ink-500 overflow-hidden bg-ink-900">
        <div className="flex animate-marquee whitespace-nowrap py-6">
          {[...CAPABILITIES, ...CAPABILITIES].map((cap, i) => (
            <div key={i} className="flex items-center gap-16 px-8">
              <span className="font-display font-bold text-2xl md:text-3xl text-ash-300 tracking-tight">
                {cap}
              </span>
              <span className="text-brand text-3xl">/</span>
            </div>
          ))}
        </div>
      </section>

      {/* STATS / WHY DOSOFT */}
      <section data-testid="stats-section" className="py-24 md:py-32 border-b border-ink-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-widest text-brand font-mono mb-6">
                01 / Quiénes somos
              </div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-ash-100 leading-tight">
                Rápidos, competitivos<br />y confiables.
              </h2>
              <p className="text-ash-300 mt-8 leading-relaxed text-lg">
                Entregamos productos digitales que se usan todos los días, con
                ciclos cortos y precios competitivos. Construimos pilotos en
                semanas para validar antes de escalar, y usamos IA para validar en
                tiempo real todos los avances.
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                <span className="px-3 py-2 border border-brand text-brand text-xs uppercase tracking-widest font-mono inline-flex items-center gap-2">
                  <Sparkles size={12} strokeWidth={1.5} /> IA-Friendly
                </span>
                <span className="px-3 py-2 border border-ink-500 text-ash-100 text-xs uppercase tracking-widest font-mono">
                  Entregas cada 2 semanas
                </span>
                <span className="px-3 py-2 border border-ink-500 text-ash-100 text-xs uppercase tracking-widest font-mono">
                  Precios competitivos
                </span>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-ink-500 border border-ink-500">
              {STATS.map((s, i) => (
                <div key={i} className="bg-ink-800 p-8 md:p-10">
                  <div className="font-display font-black text-5xl md:text-6xl text-brand tracking-tighter">
                    {s.value}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-ash-300 mt-3 font-mono">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24 md:py-32 border-b border-ink-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand font-mono mb-6">
                02 / Capacidades
              </div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-ash-100">
                Qué hacemos.
              </h2>
            </div>
            <Link to="/servicios" data-testid="services-view-all" className="text-xs uppercase tracking-widest text-ash-300 hover:text-brand hover-line">
              Ver todos los servicios →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-500 border border-ink-500">
            {services.map((s, i) => {
              const Icon = ICONS[s.icon] || Code2;
              return (
                <div
                  key={s.id}
                  data-testid={`service-card-${s.slug}`}
                  className="bg-ink-800 p-6 group hover:bg-ink-700 transition-colors relative overflow-hidden"
                >
                  <div className="text-[10px] font-mono text-ash-500 mb-4">0{i + 1}</div>
                  <Icon size={22} strokeWidth={1.5} className="text-brand mb-4" />
                  <h3 className="font-display font-bold text-lg text-ash-100 mb-2 leading-tight">
                    {s.title}
                  </h3>
                  <p className="text-ash-300 mb-4 leading-relaxed text-sm">{s.subtitle}</p>
                  <ul className="space-y-1.5">
                    {s.features.slice(0, 3).map((f, j) => (
                      <li key={j} className="text-[12px] text-ash-300 flex items-start gap-2 leading-snug">
                        <span className="text-brand mt-1">—</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <ArrowUpRight size={16} strokeWidth={1.5} className="text-ash-500 group-hover:text-brand absolute top-6 right-6 transition-colors" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section data-testid="projects-section" className="py-24 md:py-32 border-b border-ink-500 bg-ink-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-widest text-brand font-mono mb-6">
                03 / Trabajo reciente
              </div>
              <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-ash-100">
                Casos de éxito.
              </h2>
            </div>
            <Link to="/casos" data-testid="projects-view-all" className="text-xs uppercase tracking-widest text-ash-300 hover:text-brand hover-line">
              Ver todos los casos →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.slice(0, 4).map((p) => (
              <Link
                key={p.id}
                to={`/casos/${p.slug}`}
                data-testid={`project-card-${p.slug}`}
                className="group border border-ink-500 hover:border-brand transition-colors bg-ink-800"
              >
                <div className="aspect-[16/10] overflow-hidden bg-ink-700">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4 text-xs uppercase tracking-widest font-mono text-ash-500">
                    <span>{p.industry}</span>
                    <span>{p.year}</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl text-ash-100 mb-3 group-hover:text-brand transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-ash-300 text-sm leading-relaxed line-clamp-2 mb-5">
                    {p.summary}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {p.tech_stack.slice(0, 4).map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-ink-500 text-ash-300 font-mono">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="process-section" className="py-24 md:py-32 border-b border-ink-500">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-6">
            04 / Metodología
          </div>
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-ash-100 mb-16">
            Cómo trabajamos.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-500 border border-ink-500">
            {[
              { icon: Lightbulb, n: "01", t: "Discovery", d: "Inmersión en tu operación: procesos, datos, dolores reales en terreno." },
              { icon: GitBranch, n: "02", t: "Diseño", d: "Arquitectura técnica, prototipo navegable y plan de entrega por sprints." },
              { icon: Code2, n: "03", t: "Build", d: "Equipo dedicado entregando incrementos cada 2 semanas con QA continuo." },
              { icon: Shield, n: "04", t: "Operación", d: "Soporte 24/7, observabilidad, evolución continua y SLA garantizado." },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.n} className="bg-ink-800 p-8 md:p-10">
                  <div className="text-xs font-mono text-brand mb-6">{p.n}</div>
                  <Icon size={28} strokeWidth={1.5} className="text-ash-100 mb-6" />
                  <h3 className="font-display font-bold text-2xl text-ash-100 mb-3">{p.t}</h3>
                  <p className="text-ash-300 text-sm leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="border border-ink-500 bg-ink-900 p-10 md:p-20 noise relative">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="text-xs uppercase tracking-widest text-brand font-mono mb-6 flex items-center gap-3">
                  <Zap size={14} strokeWidth={1.5} />
                  Listo para empezar
                </div>
                <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-ash-100 leading-[0.95]">
                  ¿Tienes un sistema que<br />
                  <span className="text-brand">debe construirse bien</span>?
                </h2>
                <p className="text-ash-300 mt-6 max-w-xl leading-relaxed text-lg">
                  Conversemos. Te entregamos una propuesta técnica y comercial en menos de 5 días hábiles.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link
                  to="/cotizador"
                  data-testid="cta-quote"
                  className="bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-8 py-4 text-center uppercase tracking-wider text-sm"
                >
                  Solicitar cotización
                </Link>
                <Link
                  to="/contacto"
                  data-testid="cta-contact"
                  className="border border-ink-500 hover:border-brand text-ash-100 px-8 py-4 text-center uppercase tracking-wider text-sm transition-colors"
                >
                  Hablar con nuestro equipo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
